import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { permissionService } from '../../services/permissionService';
import { tableService } from '../../services/tableService';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import './Tables.css';

const UserTableDetails = () => {
  const { tableName } = useParams();
  const [table, setTable] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState('');
  const [rowForm, setRowForm] = useState({});
  const [latestOnly, setLatestOnly] = useState(false);
  const [rowLimit, setRowLimit] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);
  const [isInsertDataOpen, setIsInsertDataOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadTable = async () => {
      setLoading(true);
      const result = await tableService.getTableSchema(tableName);
      if (result.success) {
        setTable(result.table);
        setError('');
      } else {
        setError(result.error);
      }
      setLoading(false);
    };

    loadTable();
  }, [tableName]);

  useEffect(() => {
    const loadPermissions = async () => {
      const result = await permissionService.getCurrentUser();
      if (result.success) {
        setPermissions(result.permissions || []);
      }
    };

    loadPermissions();
  }, []);

  useEffect(() => {
    if (table) {
      const initialForm = {};
      (table.schema_definition?.columns || []).forEach((column) => {
        initialForm[column.name] = '';
      });
      setRowForm(initialForm);
    }
  }, [table]);

  const tablePermission = useMemo(
    () => permissions.find((permission) => permission.table_name === tableName),
    [permissions, tableName]
  );

  const canInsert = tablePermission?.can_insert ?? false;
  const canDelete = tablePermission?.can_delete ?? false;

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    const result = await dataService.listRows(tableName, {
      limit: rowLimit,
      offset: currentPage * rowLimit,
      latest: latestOnly,
      sortBy: sortBy || undefined,
      sortDir,
    });

    if (result.success) {
      setRows(result.rows);
      setRowsTotal(result.totalCount || 0);
      setRowsError('');
    } else {
      setRowsError(result.error);
    }
    setRowsLoading(false);
  }, [currentPage, latestOnly, rowLimit, sortBy, sortDir, tableName]);

  useEffect(() => {
    if (table) {
      loadRows();
    }
  }, [table, loadRows]);

  const totalPages = Math.max(1, Math.ceil(rowsTotal / rowLimit));

  const handleSort = (columnName) => {
    setLatestOnly(false);
    setCurrentPage(0);
    setSortDir((prev) => (sortBy === columnName ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortBy(columnName);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 0 || nextPage >= totalPages) return;
    setCurrentPage(nextPage);
  };

  const parseInputValue = (value, type) => {
    if (value === '') return undefined;
    const normalizedType = (type || '').toLowerCase().replace(/\(.*\)/, '').trim();
    if (['integer', 'bigint', 'smallint', 'decimal', 'numeric', 'real', 'double precision'].includes(normalizedType)) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }
    if (['boolean', 'bool'].includes(normalizedType)) {
      return value === 'true' || value === '1' || value === 'yes';
    }
    if (['json', 'jsonb'].includes(normalizedType)) {
      try {
        return JSON.parse(value);
      } catch (error) {
        throw new Error('Invalid JSON payload');
      }
    }
    return value;
  };

  const handleRowSubmit = async (event) => {
    event.preventDefault();
    if (!canInsert) {
      setRowsError('You do not have insert permission for this table.');
      return;
    }

    const payload = {};
    try {
      (table.schema_definition?.columns || []).forEach((column) => {
        const rawValue = rowForm[column.name];
        if (rawValue !== '' && rawValue !== undefined) {
          payload[column.name] = parseInputValue(rawValue, column.type);
        }
      });
    } catch (error) {
      setRowsError(error.message || 'Invalid row payload');
      return;
    }

    const result = await dataService.insertRow(tableName, payload);
    if (result.success) {
      loadRows();
      setRowForm((prev) => {
        const cleared = {};
        Object.keys(prev).forEach((key) => {
          cleared[key] = '';
        });
        return cleared;
      });
    } else {
      setRowsError(result.error);
    }
  };

  const primaryKeyColumn = useMemo(() => {
    if (!table) return null;
    return (table.schema_definition?.columns || []).find((col) => col.primary_key);
  }, [table]);

  const displayColumns = useMemo(() => {
    const schemaColumns = (table?.schema_definition?.columns || []).map((c) => c.name);
    const extraColumns = rows.length > 0
      ? Object.keys(rows[0]).filter((key) => !schemaColumns.includes(key))
      : [];
    const combinedColumns = [...schemaColumns, ...extraColumns];
    if (!combinedColumns.includes('username')) return combinedColumns;
    const columnsWithoutUsername = combinedColumns.filter((column) => column !== 'username');
    columnsWithoutUsername.splice(Math.min(1, columnsWithoutUsername.length), 0, 'username');
    return columnsWithoutUsername;
  }, [rows, table]);

  const handleRowDelete = async (row) => {
    if (!primaryKeyColumn) {
      setRowsError('No primary key available to delete rows.');
      return;
    }
    if (!canDelete) {
      setRowsError('You do not have delete permission for this table.');
      return;
    }

    const rowId = row[primaryKeyColumn.name];
    if (rowId === undefined) {
      setRowsError('Selected row has no primary key value.');
      return;
    }

    if (window.confirm(`Delete row ${rowId}?`)) {
      const result = await dataService.deleteRow(tableName, rowId);
      if (result.success) {
        loadRows();
      } else {
        setRowsError(result.error);
      }
    }
  };

  return (
    <div className="tables-page">
      <header className="tables-header">
        <div className="header-content">
          <HeaderNewTabButton />
          <div>
            <h1>Table Viewer</h1>
            <p>{tableName}</p>
          </div>
          <div className="tables-header-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/user')}>
              Back to My Tables
            </button>
          </div>
        </div>
      </header>

      <main className="tables-main">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading table details...</p>
          </div>
        ) : error ? (
          <div className="notification error">{error}</div>
        ) : (
          <div className="table-details-layout">
            <section className="table-details-card">
              <div className="card-header-row">
                <h2>Schema</h2>
                <button
                  type="button"
                  className="card-toggle"
                  aria-expanded={isSchemaOpen}
                  aria-label={isSchemaOpen ? 'Collapse schema' : 'Expand schema'}
                  onClick={() => setIsSchemaOpen((prev) => !prev)}
                >
                  {isSchemaOpen ? '▴' : '▾'}
                </button>
              </div>
              {isSchemaOpen && (
                <>
                  <p>{table.description || 'No description provided.'}</p>
                  <div className="schema-list">
                    {(table.schema_definition?.columns || []).map((column) => (
                      <div key={column.name} className="schema-item">
                        <div>
                          <strong>{column.name}</strong>
                          <span className="schema-type">{column.type}</span>
                        </div>
                        <div className="schema-meta">
                          {column.primary_key && <span className="schema-tag">PK</span>}
                          {!column.nullable && <span className="schema-tag">Required</span>}
                          {column.unique && <span className="schema-tag">Unique</span>}
                          {column.default && <span className="schema-tag">Default</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className="table-details-card">
              <div className="card-header-row">
                <h2>Insert Data</h2>
                <button
                  type="button"
                  className="card-toggle"
                  aria-expanded={isInsertDataOpen}
                  aria-label={isInsertDataOpen ? 'Collapse insert data' : 'Expand insert data'}
                  onClick={() => setIsInsertDataOpen((prev) => !prev)}
                >
                  {isInsertDataOpen ? '▴' : '▾'}
                </button>
              </div>
              {isInsertDataOpen && (
                <>
                  <p>Add a row if your permissions allow inserts.</p>
                  {rowsError && <div className="notification error">{rowsError}</div>}
                  <form className="row-form" onSubmit={handleRowSubmit}>
                    {(table.schema_definition?.columns || []).map((column) => (
                      <div key={column.name} className="form-group">
                        <label>
                          {column.name}
                          {!column.nullable && !column.primary_key && <span className="required">*</span>}
                        </label>
                        <input
                          type="text"
                          value={rowForm[column.name] || ''}
                          onChange={(event) =>
                            setRowForm((prev) => ({
                              ...prev,
                              [column.name]: event.target.value,
                            }))
                          }
                          placeholder={column.type}
                        />
                      </div>
                    ))}
                    <button className="btn btn-primary" type="submit" disabled={!canInsert}>
                      {canInsert ? 'Insert Row' : 'Insert Disabled'}
                    </button>
                  </form>
                </>
              )}
            </section>

            <section className="table-details-card">
              <div className="data-header">
                <div>
                  <h2>View Data</h2>
                  <p>Browse the current contents of this table.</p>
                </div>
                <div className="data-controls">
                  <label>
                    Latest only
                    <input
                      type="checkbox"
                      checked={latestOnly}
                      onChange={(event) => {
                        setLatestOnly(event.target.checked);
                        setCurrentPage(0);
                        if (event.target.checked) {
                          setSortBy('');
                        }
                      }}
                    />
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={rowLimit}
                    onChange={(event) => {
                      setRowLimit(Number(event.target.value));
                      setCurrentPage(0);
                    }}
                  />
                  <button className="btn btn-secondary" onClick={loadRows}>
                    Refresh
                  </button>
                </div>
              </div>

              {rowsError && <div className="notification error">{rowsError}</div>}

              {rowsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading rows...</p>
                </div>
              ) : (
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        {displayColumns.map((columnName) => (
                          <th key={columnName}>
                            <button
                              type="button"
                              className="sortable-header"
                              onClick={() => handleSort(columnName)}
                            >
                              {columnName}
                              {sortBy === columnName && (
                                <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>
                              )}
                            </button>
                          </th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`row-${index}`}>
                          {displayColumns.map((columnName) => (
                            <td key={`${index}-${columnName}`}>
                              {row[columnName] !== undefined ? String(row[columnName]) : ''}
                            </td>
                          ))}
                          <td>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => handleRowDelete(row)}
                              disabled={!primaryKeyColumn || !canDelete}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </button>
                  </div>
                  {rows.length === 0 && (
                    <div className="empty-state">
                      <p>No rows found.</p>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default UserTableDetails;
